import { useEffect, useState } from "react";

import { FeedbackContentStep } from "./steps/feedback-content-step";
import { FeedbackSuccessStep } from "./steps/feedback-success-step";
import { FeedbackTypeStep } from "./steps/feedback-type-step";

import bugImageUrl from "../../assets/bug_report.svg";
import ideaImageUrl from "../../assets/emoji_objects.svg";
import thoughtImageUrl from "../../assets/psychology_alt.svg";

import { initFeedget } from "../../lib/api";

export const feedbackTypes = {
  BUG: {
    title: "Problema",
    image: {
      source: bugImageUrl,
      alt: "Imagem de um inseto",
    },
  },
  IDEA: {
    title: "Ideia",
    image: {
      source: ideaImageUrl,
      alt: "Imagem de uma lâmpada",
    },
  },
  OTHER: {
    title: "Outro",
    image: {
      source: thoughtImageUrl,
      alt: "Imagem de um balão de pensamento",
    },
  },
};
export type FeedbackType = keyof typeof feedbackTypes;

interface WidgetFormProps {
  apiKey?: string;
  apiUrl?: string;
  showWatermark?: boolean;
  metadata?: {
    userId?: string;
    version?: string;
    custom?: Record<string, any>;
  };
}

export function WidgetForm({
  apiKey,
  apiUrl,
  showWatermark = true,
  metadata,
}: WidgetFormProps) {
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(null);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (apiKey) {
      try {
        initFeedget({
          apiKey,
          apiUrl,
          metadata,
        });
        setError(null);
      } catch (err) {
        setError("Erro ao inicializar Feedget");
        console.error("Erro ao inicializar Feedget:", err);
      }
    } else {
      setError("API Key é obrigatória para usar o widget");
    }
  }, [apiKey, apiUrl, metadata]);

  const handleRestartFeedback = () => {
    setFeedbackSent(false);
    setFeedbackType(null);
  };

  if (error) {
    return (
      <div className="bg-zinc-900 p-4 relative rounded-[5px] mb-4 flex flex-col items-center shadow-lg w-[calc(100vw-2rem)] md:w-auto">
        <div className="text-center text-red-400 p-4">
          <p className="text-sm">{error}</p>
          <p className="text-xs text-zinc-400 mt-2">
            Verifique sua configuração de API Key
          </p>
        </div>
        {showWatermark && (
          <footer className="text-sm text-neutral-400">
            Feito por{" "}
            <a
              href="https://feedget.io"
              target="_blank"
              className="underline underline-offset-2 hover:text-zinc-100"
            >
              Feedget.
            </a>
          </footer>
        )}
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/95 backdrop-blur-sm p-4 relative rounded-2xl mb-4 flex flex-col items-center shadow-2xl w-[calc(100vw-2rem)] md:w-auto border border-zinc-800/50 overflow-hidden ring-1 ring-white/5 group">
      {/* Gradient top border highlight */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-brand-500/50 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
      
      {feedbackSent ? (
        <FeedbackSuccessStep
          onFeedbackRestartRequested={handleRestartFeedback}
        />
      ) : (
        <>
          {!feedbackType ? (
            <FeedbackTypeStep onFeedbackTypeChanged={setFeedbackType} />
          ) : (
            <FeedbackContentStep
              feedbackType={feedbackType}
              onFeedbackRestartRequested={handleRestartFeedback}
              onFeedbackSent={() => setFeedbackSent(true)}
            />
          )}
        </>
      )}

      {showWatermark && (
        <footer className="text-xs text-zinc-500 mt-2">
          Feito com ♥ pela{" "}
          <a
            href="https://feedget.io"
            target="_blank"
            className="underline underline-offset-2 hover:text-brand-500 transition-colors"
          >
            Feedget
          </a>
        </footer>
      )}
    </div>
  );
}
